import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { UsageTracker } from "../services/usageTracker";
import { executeRealtimeTool } from "../services/realtimeToolExecutor";
import { reportClientError } from "../services/errorReporter";

const IDLE_RECOVERY_COOLDOWN_MS = 15000;
const REMOTE_AUDIO_SPEAKING_THRESHOLD = 6;
const REMOTE_AUDIO_OUTPUT_VOLUME = 0.65;

/**
 * useWebRTC
 * ----------
 * Handles the full lifecycle of an OpenAI‑Realtime WebRTC connection
 * and provides helper functions for the UI.
 */
export const useWebRTC = ({
  conversation,
  conversationMode,
  audioMode,
  onMessageReceived,
  setIsTalking,
  setMicVolume,
  setRemoteAudioVolume,
  onUsageUpdate, // Optional callback for usage updates
  onGuardrailEvent, // Optional callback for guardrail events (warning/final/session_ended)
}) => {
  const hookId = useRef(Math.random().toString(36).substr(2, 9));
  // console.log(`🎤 useWebRTC hook instance ${hookId.current} initialized`);
  
  const pcRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);
  const usageTrackerRef = useRef(null);
  const latestSessionTokenRef = useRef(null);
  const latestConversationRef = useRef(null);
  const latestToolsRef = useRef([]);
  const latestRealtimeConfigRef = useRef(null);
  const remoteAudioElementRef = useRef(null);
  const remoteAudioContextRef = useRef(null);
  const localAudioContextRef = useRef(null);
  const remoteAudioFrameRef = useRef(null);
  const micAudioFrameRef = useRef(null);
  const cleanupRef = useRef(null);
  const lastRemoteVolumeSampleRef = useRef({ time: 0, value: 0 });
  const lastMicVolumeSampleRef = useRef({ time: 0, value: 0 });
  const aiReplyCountRef = useRef(0);
  const sessionLimitsRef = useRef(null);
  const guardStateRef = useRef({
    input_tokens_used: 0,
    output_tokens_used: 0,
    warning_sent: false,
    final_warning_sent: false,
    cutoff_initiated: false,
    cutoff_trigger_reply_index: null,
    session_terminated: false,
  });
  const initialResponseSentRef = useRef(false);
  const idleRecoveryTimerRef = useRef(null);
  const lastRealtimeActivityAtRef = useRef(Date.now());
  const lastIdleRecoveryAtRef = useRef(0);
  const playerSpeakingRef = useRef(false);
  const responseActiveRef = useRef(false);
  const remoteAudioSpeakingRef = useRef(false);
  const toolCallActiveCountRef = useRef(0);

  const [localStreamState, setLocalStreamState] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('idle');
  // Track how many ICE restarts we’ve attempted during this session
  const iceRestartAttemptsRef = useRef(0);

  useEffect(() => {
    usageTrackerRef.current = new UsageTracker(conversationMode);
  }, [conversationMode]);

  const getResponseModalities = (realtimeConfig = latestRealtimeConfigRef.current) => {
    if (Array.isArray(realtimeConfig?.outputModalities) && realtimeConfig.outputModalities.length > 0) {
      return realtimeConfig.outputModalities;
    }

    if (conversationMode === "text") {
      return ["text"];
    }

    if (conversationMode === "voice") {
      return ["audio"];
    }

    return ["audio"];
  };

  const buildSessionConfig = (
    realtimeConfig = latestRealtimeConfigRef.current,
    instructions = latestConversationRef.current?.[0]?.content || "",
    tools = latestToolsRef.current,
  ) => {
    const outputModalities = getResponseModalities(realtimeConfig);
    const session = {
      type: "realtime",
      model: realtimeConfig?.model || 'gpt-realtime-1.5',
      instructions,
      output_modalities: outputModalities,
      tool_choice: "auto",
      parallel_tool_calls: false,
      tracing: "auto",
    };

    if (Array.isArray(tools) && tools.length > 0) {
      session.tools = tools;
    }

    if (outputModalities.includes("audio")) {
      session.audio = {
        input: {
          format: {
            type: "audio/pcm",
            rate: 24000,
          },
          noise_reduction: {
            type: "near_field",
          },
          turn_detection: {
            type: "semantic_vad",
            eagerness: "high",
            create_response: true,
            interrupt_response: true,
          },
        },
        output: {
          format: {
            type: "audio/pcm",
            rate: 24000,
          },
          voice: realtimeConfig?.voice || 'ash',
          speed: 1.0,
        },
      };
    }

    return session;
  };

  // Helper function to process usage data from OpenAI responses
  const processUsageData = (responseData) => {
    try {
      if (responseData.type === "response.done" && responseData.response?.usage) {
        const usage = responseData.response.usage;

        console.log('Realtime response usage:\n' + JSON.stringify(usage, null, 2));
        const inputTokenDetails = usage.input_token_details || {};
        const outputTokenDetails = usage.output_token_details || {};
        const cachedTokenDetails = inputTokenDetails.cached_tokens_details || {};
        
        // Extract cached tokens from input_token_details if available
        const cachedTokens = inputTokenDetails.cached_tokens ||
                           usage.cached_tokens || 
                           inputTokenDetails.cache_read_input_tokens || 0;
        
        // Validate and extract usage data
        const usageData = {
          inputTokens: usage.input_tokens || 0,
          cachedTokens: cachedTokens,
          outputTokens: usage.output_tokens || 0,
          inputTextTokens: inputTokenDetails.text_tokens || 0,
          inputAudioTokens: inputTokenDetails.audio_tokens || 0,
          cachedTextTokens: cachedTokenDetails.text_tokens || 0,
          cachedAudioTokens: cachedTokenDetails.audio_tokens || 0,
          outputTextTokens: outputTokenDetails.text_tokens || 0,
          outputAudioTokens: outputTokenDetails.audio_tokens || 0,
          usageEvent: {
            responseId: responseData.response?.id || null,
            capturedAt: new Date().toISOString(),
            usage
          }
        };
        
        //console.log("🔍 DEBUG: Processed usage data:", usageData);

        // Update usage tracker
        if (usageTrackerRef.current) {
          usageTrackerRef.current.updateUsage(usageData);

          const totalUsage = usageTrackerRef.current.getTotalUsage();

          // Update in-memory guard counters using "new" input (excludes cached) and output
          const guard = guardStateRef.current;
          guard.input_tokens_used = totalUsage.newInputTokens;
          guard.output_tokens_used = totalUsage.outputTokens;

          // Evaluate guardrails on response boundaries only
          enforceGuardrails(totalUsage);

          // Call optional callback with updated usage
          if (onUsageUpdate) {
            onUsageUpdate(totalUsage);
          }
        }
      }
    } catch (error) {
      console.error('Error processing usage data:', error);
      // Continue operation without breaking
    }
  };

  const computeMaxRatio = (totals) => {
    const limits = sessionLimitsRef.current;
    if (!limits) return 0;
    const maxIn = Number(limits.max_input_tokens) || 0;
    const maxOut = Number(limits.max_output_tokens) || 0;
    const inUsed = totals?.newInputTokens ?? guardStateRef.current.input_tokens_used;
    const outUsed = totals?.outputTokens ?? guardStateRef.current.output_tokens_used;
    const inRatio = maxIn > 0 ? inUsed / maxIn : 0;
    const outRatio = maxOut > 0 ? outUsed / maxOut : 0;
    return Math.max(inRatio, outRatio);
  };

  const sendSystemNudge = (text) => {
    try {
      const dc = dataChannelRef.current;
      if (!dc || dc.readyState !== 'open') return false;
      // Create a system message and then request a new response
      const systemMsg = {
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [{ type: 'input_text', text }],
        },
      };
      dc.send(JSON.stringify(systemMsg));

      const responsePayload = {
        type: 'response.create',
        response: {
          output_modalities: getResponseModalities(),
          max_output_tokens: 1024,
        },
      };
      dc.send(JSON.stringify(responsePayload));
      return true;
    } catch (e) {
      console.warn('Failed to send system nudge:', e);
      return false;
    }
  };

  const clearIdleRecoveryTimer = () => {
    if (idleRecoveryTimerRef.current) {
      clearTimeout(idleRecoveryTimerRef.current);
      idleRecoveryTimerRef.current = null;
    }
  };

  const getIdleRecoveryConfig = () => {
    const rawConfig = latestRealtimeConfigRef.current?.idleRecovery || {};
    const delayMs = Number(rawConfig.delay_ms || rawConfig.delayMs || rawConfig.idle_recovery_delay_ms) || 7000;

    return {
      enabled: Boolean(rawConfig.enabled || rawConfig.idle_recovery_enabled),
      delayMs: Math.max(1, delayMs),
      prompt: typeof rawConfig.prompt === 'string' ? rawConfig.prompt.trim() : '',
      gameMode: rawConfig.game_mode || rawConfig.gameMode || null,
      gameModeName: rawConfig.game_mode_name || rawConfig.gameModeName || null,
      gameSessionId: latestRealtimeConfigRef.current?.gameSessionId || rawConfig.game_session_id || rawConfig.gameSessionId || null,
    };
  };

  const isHostSpeakingOrResponding = () => (
    responseActiveRef.current ||
    remoteAudioSpeakingRef.current ||
    (lastRemoteVolumeSampleRef.current?.value || 0) > REMOTE_AUDIO_SPEAKING_THRESHOLD
  );

  const sendIdleRecoveryNudge = (config, silenceDurationMs) => {
    const timestamp = new Date().toISOString();
    const sent = sendSystemNudge(config.prompt);

    if (!sent) {
      return false;
    }

    reportClientError({
      message: 'Idle recovery triggered',
      error: new Error('Idle recovery triggered'),
      severity: 'info',
      category: 'idle_recovery_triggered',
      gameSessionId: config.gameSessionId,
      metadata: {
        timestamp,
        game_session_id: config.gameSessionId,
        game_mode: config.gameMode || config.gameModeName,
        silence_duration_ms: silenceDurationMs,
        idle_recovery_prompt: config.prompt,
      },
    });

    return true;
  };

  const scheduleIdleRecoveryCheck = () => {
    clearIdleRecoveryTimer();

    const config = getIdleRecoveryConfig();
    const dc = dataChannelRef.current;

    if (
      guardStateRef.current.session_terminated ||
      !config.enabled ||
      !config.prompt ||
      !dc ||
      dc.readyState !== 'open'
    ) {
      return;
    }

    const now = Date.now();
    const inactivityStartedAt = lastRealtimeActivityAtRef.current || now;
    const silenceDurationMs = playerSpeakingRef.current ? 0 : now - inactivityStartedAt;
    const cooldownRemainingMs = Math.max(0, IDLE_RECOVERY_COOLDOWN_MS - (now - lastIdleRecoveryAtRef.current));
    const silenceRemainingMs = Math.max(0, config.delayMs - silenceDurationMs);
    const busyDelayMs = (isHostSpeakingOrResponding() || toolCallActiveCountRef.current > 0) ? 250 : 0;
    const nextDelayMs = Math.max(silenceRemainingMs, cooldownRemainingMs, busyDelayMs, 100);

    idleRecoveryTimerRef.current = setTimeout(() => {
      const latestConfig = getIdleRecoveryConfig();
      const latestDc = dataChannelRef.current;
      const latestNow = Date.now();
      const latestSilenceDurationMs = latestNow - (lastRealtimeActivityAtRef.current || latestNow);
      const cooldownActive = latestNow - lastIdleRecoveryAtRef.current < IDLE_RECOVERY_COOLDOWN_MS;

      if (
        guardStateRef.current.session_terminated ||
        !latestConfig.enabled ||
        !latestConfig.prompt ||
        !latestDc ||
        latestDc.readyState !== 'open' ||
        playerSpeakingRef.current ||
        latestSilenceDurationMs < latestConfig.delayMs ||
        isHostSpeakingOrResponding() ||
        toolCallActiveCountRef.current > 0 ||
        cooldownActive
      ) {
        scheduleIdleRecoveryCheck();
        return;
      }

      lastIdleRecoveryAtRef.current = latestNow;
      lastRealtimeActivityAtRef.current = latestNow;
      sendIdleRecoveryNudge(latestConfig, latestSilenceDurationMs);
      scheduleIdleRecoveryCheck();
    }, nextDelayMs);
  };

  const handleRealtimeActivityEvent = (data) => {
    if (!data?.type) {
      return;
    }

    lastRealtimeActivityAtRef.current = Date.now();

    if (data.type === "input_audio_buffer.speech_started") {
      playerSpeakingRef.current = true;
      clearIdleRecoveryTimer();
      return;
    }

    if (
      data.type === "input_audio_buffer.speech_stopped" ||
      data.type === "input_audio_buffer.committed"
    ) {
      playerSpeakingRef.current = false;
      scheduleIdleRecoveryCheck();
      return;
    }

    if (data.type === "response.created") {
      responseActiveRef.current = true;
      clearIdleRecoveryTimer();
      return;
    }

    if (data.type === "response.done") {
      responseActiveRef.current = false;
      scheduleIdleRecoveryCheck();
      return;
    }

    if (!responseActiveRef.current && !playerSpeakingRef.current && toolCallActiveCountRef.current === 0) {
      scheduleIdleRecoveryCheck();
    }
  };

  const enforceGuardrails = (totals) => {
    const limits = sessionLimitsRef.current;
    if (!limits) return;
    const guard = guardStateRef.current;
    if (guard.session_terminated) return;

    const ratio = computeMaxRatio(totals);
    const warnThreshold = Number(limits.warning_threshold) || 0.8;

    // Final wrap-up (prioritize this if both thresholds are crossed in one step)
    if (!guard.final_warning_sent && ratio >= 0.95) {
      guard.final_warning_sent = true;
      usageTrackerRef.current?.updateUsage({
        usageEvent: { event: 'final_warning_triggered', ratio, timestamp: new Date().toISOString() },
      });
      onGuardrailEvent && onGuardrailEvent({ type: 'final_warning', ratio });
      sendSystemNudge(
        'We are at the finish line and our time slot is nearly up. Move directly into your final round or summary, then deliver warm, satisfying closing remarks and clearly end the game. Do not mention tokens or internal limits.'
      );
      // no return; still allow cutoff check below in case we already hit the limit
    }

    // Soft wrap-up (only if final not already sent this turn)
    if (!guard.warning_sent && ratio >= warnThreshold && !guard.final_warning_sent) {
      guard.warning_sent = true;
      usageTrackerRef.current?.updateUsage({
        usageEvent: { event: 'warning_triggered', ratio, timestamp: new Date().toISOString() },
      });
      onGuardrailEvent && onGuardrailEvent({ type: 'warning', ratio });
      sendSystemNudge(
        'We are getting close to the end of our time. Begin wrapping up naturally: transition toward a final round, keep energy up, and set up your closing remarks. Do not mention tokens or internal limits.'
      );
      return;
    }

    // Hard cutoff
    const maxIn = Number(limits.max_input_tokens) || 0;
    const maxOut = Number(limits.max_output_tokens) || 0;
    const hitCutoff = (maxIn > 0 && guard.input_tokens_used >= maxIn) || (maxOut > 0 && guard.output_tokens_used >= maxOut);

    if (!guard.cutoff_initiated && hitCutoff) {
      guard.cutoff_initiated = true;
      guard.cutoff_trigger_reply_index = (aiReplyCountRef.current || 0) + 1;
      usageTrackerRef.current?.updateUsage({
        usageEvent: { event: 'cutoff_initiated', ratio, timestamp: new Date().toISOString() },
      });
      sendSystemNudge(
        'This is your last turn — our time is up. Conclude right now with a brief, gracious outro: thank the players, optionally announce the winner if appropriate, and say goodbye. Do not mention tokens or internal limits.'
      );
    }

    // If we previously initiated cutoff and we have reached the scheduled next reply, terminate
    if (guard.cutoff_initiated && typeof guard.cutoff_trigger_reply_index === 'number') {
      const reached = (aiReplyCountRef.current || 0) >= guard.cutoff_trigger_reply_index;
      if (!reached) return;
      // We’re on a response boundary; proceed to terminate
      guard.cutoff_trigger_reply_index = null;
      guard.session_terminated = true;
      usageTrackerRef.current?.updateUsage({
        usageEvent: { event: 'session_ended', reason: 'token_limit', timestamp: new Date().toISOString() },
      });

      // Let the app orchestrate teardown so audio is not cut mid‑playback
      onGuardrailEvent && onGuardrailEvent({ type: 'session_ended', reason: 'token_limit' });
    }
  };

  const closeAudioResources = async () => {
    if (remoteAudioFrameRef.current) {
      cancelAnimationFrame(remoteAudioFrameRef.current);
      remoteAudioFrameRef.current = null;
    }

    if (micAudioFrameRef.current) {
      cancelAnimationFrame(micAudioFrameRef.current);
      micAudioFrameRef.current = null;
    }

    if (remoteAudioContextRef.current) {
      try {
        await remoteAudioContextRef.current.close();
      } catch (error) {
        console.warn('Failed to close remote audio context:', error);
      }
      remoteAudioContextRef.current = null;
    }

    if (localAudioContextRef.current) {
      try {
        await localAudioContextRef.current.close();
      } catch (error) {
        console.warn('Failed to close local audio context:', error);
      }
      localAudioContextRef.current = null;
    }

    if (remoteAudioElementRef.current) {
      remoteAudioElementRef.current.pause();
      remoteAudioElementRef.current.srcObject = null;
      remoteAudioElementRef.current.remove();
      remoteAudioElementRef.current = null;
    }
  };

  const updateVolumeSample = (sampleRef, setter, nextValue) => {
    const now = performance.now();
    const roundedValue = Math.round(nextValue);
    const { time, value } = sampleRef.current;

    if (Math.abs(roundedValue - value) < 2 && now - time < 80) {
      return;
    }

    sampleRef.current = {
      time: now,
      value: roundedValue,
    };
    setter(roundedValue);
  };

  const handleRemoteAudioLevel = (avg) => {
    const now = Date.now();
    const isSpeaking = avg > REMOTE_AUDIO_SPEAKING_THRESHOLD;

    if (isSpeaking) {
      lastRealtimeActivityAtRef.current = now;

      if (!remoteAudioSpeakingRef.current) {
        remoteAudioSpeakingRef.current = true;
        clearIdleRecoveryTimer();
      }

      return;
    }

    if (remoteAudioSpeakingRef.current) {
      remoteAudioSpeakingRef.current = false;
      lastRealtimeActivityAtRef.current = now;
      scheduleIdleRecoveryCheck();
    }
  };

  /* ------------------------------------------------------------ */
  /*  initializeWebRTCConnection                                  */
  /* ------------------------------------------------------------ */
  const initializeWebRTCConnection = async (
    realtimeSessionToken,
    initialConversation = null,
    initialTools = [],
    sessionLimits = null,
    realtimeConfig = null,
  ) => {
    // If initialConversation is provided, use it instead of the conversation from props
    const conversationToUse = initialConversation || conversation;
    console.log("Initializing WebRTC Connection…");
    latestSessionTokenRef.current = realtimeSessionToken;
    latestConversationRef.current = conversationToUse;
    latestToolsRef.current = initialTools;
    sessionLimitsRef.current = sessionLimits;
    latestRealtimeConfigRef.current = realtimeConfig;
    initialResponseSentRef.current = false;
    clearIdleRecoveryTimer();
    playerSpeakingRef.current = false;
    responseActiveRef.current = false;
    remoteAudioSpeakingRef.current = false;
    toolCallActiveCountRef.current = 0;
    lastRealtimeActivityAtRef.current = Date.now();
    setConnectionStatus('connecting');

    try {
      if (dataChannelRef.current) {
        try {
          dataChannelRef.current.close();
        } catch (error) {
          console.warn('Failed to close previous data channel:', error);
        }
        dataChannelRef.current = null;
      }

      if (pcRef.current) {
        try {
          pcRef.current.close();
        } catch (error) {
          console.warn('Failed to close previous peer connection:', error);
        }
        pcRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }

      await closeAudioResources();
      lastRemoteVolumeSampleRef.current = { time: 0, value: 0 };
      lastMicVolumeSampleRef.current = { time: 0, value: 0 };
      setRemoteAudioVolume(0);
      setMicVolume(0);
      setLocalStreamState(null);

      /* ---------- PeerConnection & basic handlers ---------- */
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.onconnectionstatechange = () => {
        //console.log(`📡 WebRTC: ${pc.connectionState}`);
        if (pc.connectionState === "connected") {
          setConnectionStatus('connected');
        }

        if (pc.connectionState === "disconnected") {
          setConnectionStatus('disconnected');
          toast.error("Connection lost. Please try again.");
        }

        if (pc.connectionState === "failed") {
          setConnectionStatus('failed');
          toast.error("Connection lost. Please try again.");
        }
      };

      pc.oniceconnectionstatechange = () => {
        //console.log(`ICE State: ${pc.iceConnectionState}`);

        if (pc.iceConnectionState === "disconnected") {
          setConnectionStatus('disconnected');
          toast.warn("ICE disconnected — waiting to see if it recovers…");
          // Do nothing yet; Chrome often flips back to 'connected' by itself.
        }

        if (pc.iceConnectionState === "failed") {
          setConnectionStatus('failed');
          const attempts = iceRestartAttemptsRef.current + 1;

          if (attempts <= 2 && typeof pc.restartIce === "function") {
            iceRestartAttemptsRef.current = attempts;
            toast.info(`ICE failed — restart attempt ${attempts}`);
            try {
              pc.restartIce();
              //console.log("Called pc.restartIce()");
            } catch (err) {
              console.error("restartIce() threw", err);
            }
          } else {
            toast.error("ICE failed twice — rebuilding PeerConnection…");
            iceRestartAttemptsRef.current = 0;
            reconnectWithSession({
              realtimeSessionToken: latestSessionTokenRef.current,
              initialConversation: latestConversationRef.current,
              initialTools: latestToolsRef.current,
              sessionLimits: sessionLimitsRef.current,
              realtimeConfig: latestRealtimeConfigRef.current,
            })
              .catch((error) => {
                console.warn('ICE recovery reconnect failed:', error);
              });
          }
        }
      };

      /* ---------- Remote audio plumbing ---------- */
      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      audioEl.volume = REMOTE_AUDIO_OUTPUT_VOLUME;
      document.body.appendChild(audioEl);
      remoteAudioElementRef.current = audioEl;

      pc.ontrack = (event) => {
        audioEl.srcObject = event.streams[0];

        const audioContext = new AudioContext();
        remoteAudioContextRef.current = audioContext;
        const remoteSource = audioContext.createMediaStreamSource(event.streams[0]);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        remoteSource.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
	          analyser.getByteFrequencyData(dataArray);
	          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
	          handleRemoteAudioLevel(avg);
	          updateVolumeSample(lastRemoteVolumeSampleRef, setRemoteAudioVolume, avg);
	          remoteAudioFrameRef.current = requestAnimationFrame(tick);
        };
        tick();
      };

      /* ---------- Data channel ---------- */
      const dc = pc.createDataChannel("oai-events");
      dataChannelRef.current = dc;

      dc.onclose = () => {
        console.log("❌ Data channel closed:", dc.label);
        clearIdleRecoveryTimer();
        setConnectionStatus((current) => current === 'reconnecting' ? current : 'disconnected');
      };

      dc.onerror = (error) => {
        console.error("❌ Data channel error:", error);
        clearIdleRecoveryTimer();
        setConnectionStatus('failed');
      };

      dc.onopen = () => {
        console.log("✅ Data channel open:", dc.label);
        setConnectionStatus('connected');

        setTimeout(() => {
          if (initialResponseSentRef.current) {
            return;
          }

          if (!conversationToUse || conversationToUse.length === 0 || !conversationToUse[0]?.content) {
            console.error("❌ No conversation or system prompt available");
            return;
          }

          const sessionUpdatePayload = {
            type: "session.update",
            session: buildSessionConfig(realtimeConfig, conversationToUse[0].content, initialTools),
          };
          dc.send(JSON.stringify(sessionUpdatePayload));

          initialResponseSentRef.current = true;
          const responsePayload = {
            type: "response.create",
            response: {
              output_modalities: getResponseModalities(realtimeConfig),
              max_output_tokens: 1024,
            },
          };
          dc.send(JSON.stringify(responsePayload));
          scheduleIdleRecoveryCheck();
        }, 100);
      };

      dc.onmessage = async (event) => {
        onMessageReceived(event);
        try {
          const data = JSON.parse(event.data);
          handleRealtimeActivityEvent(data);
          
          // Maintain response count before processing guardrails
          if (data.type === "response.done") {
            aiReplyCountRef.current += 1;
          }
          // Process usage data from response.done events
          processUsageData(data);
          
          // Handle function call responses directly in useWebRTC where data channel is available
          if (data.type === "response.function_call_arguments.done" && data.call_id) {
            toolCallActiveCountRef.current += 1;
            clearIdleRecoveryTimer();

            try {
              const isTerminalToolCall = data.name === "end_game";

              if (isTerminalToolCall) {
                guardStateRef.current.session_terminated = true;
                clearIdleRecoveryTimer();
              }

              const toolOutput = await executeRealtimeTool(data.name, data.arguments);
              lastRealtimeActivityAtRef.current = Date.now();

              const functionResult = {
                type: "conversation.item.create",
                item: {
                  type: "function_call_output",
                  call_id: data.call_id,
                  output: JSON.stringify(toolOutput)
                }
              };

              dc.send(JSON.stringify(functionResult));
              setTimeout(() => {
                if (!guardStateRef.current.session_terminated && !isTerminalToolCall) {
                  const responsePayload = {
                    type: "response.create",
                    response: {
                      output_modalities: getResponseModalities(),
                      max_output_tokens: 1024
                    }
                  };
                  dc.send(JSON.stringify(responsePayload));
                }
              }, 100);
            } finally {
              toolCallActiveCountRef.current = Math.max(0, toolCallActiveCountRef.current - 1);
              if (!guardStateRef.current.session_terminated) {
                scheduleIdleRecoveryCheck();
              }
            }
          }
          // Log any server‑side errors for easier debugging
          if (data.type === "error" || data.type === "response.error") {
            console.error("Realtime API error:", data);
          }
        } catch {
          /* ignore non‑JSON or unrelated events */
        }
      };

      /* ---------- Local microphone ---------- */
      let stream;
      //console.log('🎤 Setting up local microphone, conversationMode:', conversationMode);

      if (conversationMode !== "text") {
        try {
          //console.log('🎤 Requesting getUserMedia...');
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          //console.log('🎤 getUserMedia successful, stream:', stream);
          //console.log('🎤 Audio tracks:', stream.getAudioTracks());
        } catch (error) {
          console.error('🎤 getUserMedia failed:', error);
          toast.error("Mic access denied.");
          throw new Error("Mic permission");
        }

        //console.log(`🎤 [${hookId.current}] Setting localStreamRef.current to:`, stream);
        localStreamRef.current = stream;
        setLocalStreamState(stream);
        //console.log(`🎤 [${hookId.current}] localStreamRef.current is now:`, localStreamRef.current);
        
        // Set up microphone volume analysis
        try {
          const audioContext = new AudioContext();
          localAudioContextRef.current = audioContext;
          const localSource = audioContext.createMediaStreamSource(stream);
          const localAnalyser = audioContext.createAnalyser();
          localAnalyser.fftSize = 256;

          localSource.connect(localAnalyser);

          const localDataArray = new Uint8Array(localAnalyser.frequencyBinCount);
          const micTick = () => {
            localAnalyser.getByteFrequencyData(localDataArray);
            const avg = localDataArray.reduce((a, b) => a + b, 0) / localDataArray.length;
            updateVolumeSample(lastMicVolumeSampleRef, setMicVolume, avg);
            micAudioFrameRef.current = requestAnimationFrame(micTick);
          };
          micTick();
          //console.log('🎤 Microphone volume analysis started');
        } catch (error) {
          console.error('🎤 Failed to set up microphone volume analysis:', error);
        }
        
        // Debug: Set up a watcher to see if localStreamRef gets reset
        // setTimeout(() => {
        //   console.log('🎤 [5s later] localStreamRef.current is:', localStreamRef.current);
        // }, 5000);

        if (audioMode === "pushToTalk") {
          const track = stream.getAudioTracks()[0];
          track.enabled = false; // muted until user presses
          setIsTalking(false);
          //console.log('🎤 Push-to-talk mode: track disabled');
        } else if (audioMode === "mute") {
          const track = stream.getAudioTracks()[0];
          track.enabled = false; // start in muted state for simplified audio control
          setIsTalking(false);
          //console.log('🎤 Mute mode: track disabled');
        }

        pc.addTrack(stream.getTracks()[0], stream);
        //console.log('🎤 Added track to peer connection');
      } else {
        // Text‑only mode: attach a silent track
        console.log('🎤 Text-only mode: creating silent track');
        const ctx = new AudioContext();
        const dst = ctx.createMediaStreamDestination();
        ctx.createOscillator().connect(dst);
        const silentTrack = dst.stream.getAudioTracks()[0];
        stream = new MediaStream([silentTrack]);

        localStreamRef.current = stream;
        setLocalStreamState(stream);
        pc.addTrack(silentTrack, stream);
        console.log('🎤 Silent track setup complete');
      }

      /* ---------- Offer / answer via OpenAI ---------- */
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sessionConfig = buildSessionConfig(
        realtimeConfig,
        conversationToUse[0]?.content || "",
        initialTools,
      );

      const res = await fetch('/.netlify/functions/realtime-sdp-offer', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sdp: offer.sdp,
          session: sessionConfig,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Realtime SDP exchange failed", {
          status: res.status,
          model: realtimeConfig?.model || 'gpt-realtime-1.5',
          responseBody: errorText,
        });
        toast.error("Realtime API error");
        throw new Error(`Realtime API status ${res.status}: ${errorText}`);
      }

      await pc.setRemoteDescription({ type: "answer", sdp: await res.text() });
      console.log("WebRTC ready!");
    } catch (err) {
      setConnectionStatus('failed');
      console.error(err);
      throw err;
    }
  };

  /* ---------- Push‑to‑Talk handler ---------- */
  const handlePushToTalk = (pressed) => {
    if (audioMode === "pushToTalk" && localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = pressed;
        setIsTalking(pressed);
      }
    }
  };

  /* ---------- Cleanup ---------- */
  const cleanup = async (options = {}) => {
    const { preserveSession = false, nextStatus = 'idle' } = options || {};
    console.log('🧹 WebRTC cleanup called');
    setConnectionStatus(nextStatus);
    if (dataChannelRef.current) {
      try {
        dataChannelRef.current.close();
      } catch (error) {
        console.warn('Failed to close data channel:', error);
      }
      dataChannelRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setLocalStreamState(null);
    setRemoteAudioVolume(0);
    setMicVolume(0);
    initialResponseSentRef.current = false;
    clearIdleRecoveryTimer();
    playerSpeakingRef.current = false;
    responseActiveRef.current = false;
    remoteAudioSpeakingRef.current = false;
    toolCallActiveCountRef.current = 0;
    if (!preserveSession) {
      latestSessionTokenRef.current = null;
      latestConversationRef.current = null;
      latestToolsRef.current = [];
      latestRealtimeConfigRef.current = null;
      sessionLimitsRef.current = null;
    }
    await closeAudioResources();
  };

  useEffect(() => {
    cleanupRef.current = cleanup;
  });

  const reconnectWithSession = async ({
    realtimeSessionToken = latestSessionTokenRef.current,
    initialConversation = latestConversationRef.current,
    initialTools = latestToolsRef.current,
    sessionLimits = sessionLimitsRef.current,
    realtimeConfig = latestRealtimeConfigRef.current,
  } = {}) => {
    if (!realtimeSessionToken) {
      throw new Error('Missing realtime session token for reconnect.');
    }

    setConnectionStatus('reconnecting');
    await cleanup({ preserveSession: true, nextStatus: 'reconnecting' });
    return initializeWebRTCConnection(
      realtimeSessionToken,
      initialConversation,
      initialTools,
      sessionLimits,
      realtimeConfig,
    );
  };

  const reconnectWithSessionFromArgs = async (
    realtimeSessionToken,
    initialConversation = null,
    initialTools = [],
    sessionLimits = null,
    realtimeConfig = null,
  ) => reconnectWithSession({
    realtimeSessionToken,
    initialConversation,
    initialTools,
    sessionLimits,
    realtimeConfig,
  });

  useEffect(() => {
    return () => {
      cleanupRef.current?.().catch((error) => {
        console.warn('WebRTC cleanup failed during unmount:', error);
      });
    };
  }, []);

  // Helper functions for usage tracking
  const getUsageData = () => {
    return usageTrackerRef.current ? usageTrackerRef.current.getTotalUsage() : null;
  };

  const resetUsageTracking = () => {
    if (usageTrackerRef.current) {
      usageTrackerRef.current.reset();
    }
  };

  // Helper functions for audio track management
  const getAudioTrack = () => {
    if (localStreamRef.current) {
      return localStreamRef.current.getAudioTracks()[0] || null;
    }
    return null;
  };

  const getAiReplyCount = () => aiReplyCountRef.current;

  const getConnectionSnapshot = () => ({
    peerConnectionState: pcRef.current?.connectionState || 'closed',
    iceConnectionState: pcRef.current?.iceConnectionState || 'closed',
    dataChannelState: dataChannelRef.current?.readyState || 'closed',
  });

  const isRealtimeConnectionOpen = () => {
    const connection = getConnectionSnapshot();
    return (
      connection.dataChannelState === 'open' &&
      !['failed', 'closed'].includes(connection.peerConnectionState) &&
      !['failed', 'closed'].includes(connection.iceConnectionState)
    );
  };

  const setMuted = (muted) => {
    //console.log(`🎤 [${hookId.current}] setMuted called with:`, muted);
    //console.log(`🎤 [${hookId.current}] localStreamRef.current:`, localStreamRef.current);
    
    const audioTrack = getAudioTrack();
    //console.log(`🎤 [${hookId.current}] audioTrack:`, audioTrack);
    
    if (audioTrack) {
      //console.log(`🎤 [${hookId.current}] Setting audioTrack.enabled to:`, !muted);
      audioTrack.enabled = !muted;
      //console.log(`🎤 [${hookId.current}] audioTrack.enabled is now:`, audioTrack.enabled);
      return true;
    }
    
    console.log(`🎤 [${hookId.current}] No audio track available`);
    return false;
  };

  return {
    initializeWebRTCConnection,
    reconnectWithSession,
    reconnectWithSessionFromArgs,
    cleanup,
    connectionStatus,
    handlePushToTalk,
    dataChannel: dataChannelRef.current,
    localStream: localStreamState,
    localStreamRef, // Expose the ref directly
    getAudioTrack,
    setMuted,
    getUsageData,
    resetUsageTracking,
    getAiReplyCount,
    getConnectionSnapshot,
    isRealtimeConnectionOpen,
  };
};

export default useWebRTC;
